import {
  Variable,
  CalculationResult,
  FormulaError,
  PricingTier,
  LookupTable,
  LookupTableData,
} from '../types/model.types';
import { topologicalSort } from './formula-parser';
import { VariableType } from '@/types';
import { Parser } from 'expr-eval';

/**
 * Evaluador de fórmulas
 * Usa expr-eval para evaluación segura (sin new Function / eval).
 *
 * Funciones extendidas soportadas (registradas dinámicamente sobre la instancia):
 *  - LOOKUP(tableCode, key [, columnName])  → busca el valor en una LookupTable
 *  - IF(cond, then, else)                   → condicional clásico (cond truthy ≠ 0)
 *  - IFS(cond1, val1, cond2, val2, ...)     → primer cond verdadero gana, último puede ser default
 *
 * El parser de expr-eval ya soporta:
 *  - operadores de comparación: ==, !=, <, <=, >, >=
 *  - operador ternario:        cond ? a : b
 *  - operadores lógicos:       and, or, not (también && / ||)
 */

/** Crea una instancia del parser con LOOKUP/IF/IFS y operadores de comparación. */
function buildParser(lookupTables: LookupTable[] = []): Parser {
  const parser = new Parser({
    operators: {
      logical: true,
      comparison: true,
      conditional: true,
    },
  });

  // Index lookup tables by code (case-insensitive sobre el code)
  const tablesByCode = new Map<string, LookupTable>();
  for (const lt of lookupTables) {
    if (lt.code) tablesByCode.set(lt.code.toUpperCase(), lt);
    // Permitir buscar por nombre como fallback
    if (lt.name) tablesByCode.set(lt.name.toUpperCase(), lt);
  }

  // LOOKUP(tableCode, key [, columnName]) → number
  // Si la tabla no existe o key no se encuentra → 0 (defensivo)
  // - Si la tabla tiene una sola columna, devuelve values[rowIndex][0].
  // - Si se pasa columnName, busca la columna por nombre.
  // - Si no se pasa columnName y hay múltiples columnas, devuelve la primera.
  parser.functions['LOOKUP'] = (
    tableCode: string,
    key: string | number,
    columnName?: string
  ): number => {
    if (tableCode == null || key == null) return 0;
    const code = String(tableCode).toUpperCase();
    const table = tablesByCode.get(code);
    if (!table || !table.data) return 0;
    const data = table.data as LookupTableData & { keys?: string[] };
    const rows = data.rows ?? [];
    const cols = data.cols ?? [];
    const values = data.values ?? [];

    // Buscar el rowIndex. Comparar como string (insensible a espacios laterales).
    const keyStr = String(key).trim();
    let rowIndex = rows.findIndex((r) => String(r).trim() === keyStr);
    if (rowIndex === -1) {
      // Fallback: buscar en data.keys (algunas tablas tienen 'keys' además de 'rows')
      if ((data as any).keys) {
        rowIndex = (data as any).keys.findIndex((k: string) => String(k).trim() === keyStr);
      }
    }
    if (rowIndex === -1) return 0;

    // Determinar columna
    let colIndex = 0;
    if (columnName != null) {
      const colStr = String(columnName).trim();
      const idx = cols.findIndex((c) => String(c).trim() === colStr);
      if (idx >= 0) colIndex = idx;
    }

    const row = values[rowIndex];
    if (!row) return 0;
    const v = row[colIndex];
    if (v == null || isNaN(Number(v))) return 0;
    return Number(v);
  };

  // IF(cond, then, else)
  parser.functions['IF'] = (cond: any, thenVal: any, elseVal: any) => {
    return cond ? thenVal : elseVal;
  };

  // IFS(cond1, val1, cond2, val2, ...)
  // Si la cantidad de args es impar, el último arg se trata como default.
  parser.functions['IFS'] = (...args: any[]) => {
    const len = args.length;
    if (len === 0) return 0;
    const limit = len % 2 === 0 ? len : len - 1;
    for (let i = 0; i < limit; i += 2) {
      if (args[i]) return args[i + 1];
    }
    if (len % 2 === 1) return args[len - 1];
    return 0;
  };

  // MIN/MAX/ABS/ROUND/CEIL/FLOOR ya vienen built-in en expr-eval (en lowercase).
  // Para que el usuario pueda escribirlas en mayúsculas, alias.
  for (const fn of ['MIN', 'MAX', 'ABS', 'ROUND', 'CEIL', 'FLOOR', 'SQRT', 'POW']) {
    if (!(fn in parser.functions)) {
      const lower = fn.toLowerCase();
      if (parser.functions[lower]) parser.functions[fn] = parser.functions[lower];
    }
  }

  return parser;
}

// Parser default (sin lookup tables) reusado por simuladores / playground
const defaultParser = buildParser();

/**
 * Resuelve una variable TIERED (escalonada por volumen) según la cantidad
 */
export function resolveTieredVariable(
  tiers: PricingTier[],
  quantity: number
): { unitPrice: number; discount: number; tier: PricingTier | null } {
  if (!tiers || tiers.length === 0) {
    return { unitPrice: 0, discount: 0, tier: null };
  }

  // Ordenar por minQuantity ascendente
  const sorted = [...tiers].sort((a, b) => a.minQuantity - b.minQuantity);

  // Buscar el tier que corresponde a la cantidad
  let matchedTier: PricingTier | null = null;
  for (const tier of sorted) {
    if (
      quantity >= tier.minQuantity &&
      (tier.maxQuantity == null || quantity <= tier.maxQuantity)
    ) {
      matchedTier = tier;
      break;
    }
  }

  // Si no hay match, usar el último tier (cantidad mayor)
  if (!matchedTier) {
    matchedTier = sorted[sorted.length - 1];
  }

  return {
    unitPrice: matchedTier.unitPrice ?? 0,
    discount: matchedTier.discount ?? 0,
    tier: matchedTier,
  };
}

/**
 * Resuelve una variable LOOKUP cruzando 2 dimensiones en la tabla
 */
export function resolveLookupVariable(
  data: LookupTableData,
  rowKey: string,
  colKey: string
): number | null {
  if (!data || !data.rows || !data.cols || !data.values) {
    return null;
  }

  const rowIndex = data.rows.indexOf(rowKey);
  const colIndex = data.cols.indexOf(colKey);

  if (rowIndex === -1 || colIndex === -1) {
    return null;
  }

  return data.values[rowIndex]?.[colIndex] ?? null;
}

/**
 * Traduce mensajes de error comunes de expr-eval al español
 */
function translateEvalError(message: string): string {
  if (/unexpected/i.test(message)) {
    return 'Error de sintaxis: expresión inesperada en la fórmula';
  }
  if (/expected/i.test(message) && /\)/.test(message)) {
    return 'Error de sintaxis: falta un paréntesis de cierre';
  }
  if (/expected/i.test(message) && /\(/.test(message)) {
    return 'Error de sintaxis: falta un paréntesis de apertura';
  }
  if (/undefined variable/i.test(message)) {
    const varMatch = message.match(/undefined variable[:\s]+(\w+)/i);
    return varMatch ? `Variable no definida: ${varMatch[1]}` : 'Variable no definida en la fórmula';
  }
  if (/parse error/i.test(message)) {
    return 'Error al interpretar la fórmula: verifique la sintaxis';
  }
  if (/is not a function/i.test(message)) {
    return 'La fórmula contiene una función no reconocida';
  }
  return message || 'Error desconocido al evaluar la fórmula';
}

/**
 * Conjunto de funciones / palabras reservadas que no deben tratarse como variables.
 * Importante: incluye nuestras funciones extendidas (LOOKUP/IF/IFS) y los nombres de
 * tablas que se usan como primer argumento de LOOKUP (ej. FREIGHT_TYPES) — esos se
 * detectan extrayéndolos del primer arg de cada llamada a LOOKUP en la fórmula.
 */
const RESERVED_TOKENS = new Set([
  'IF',
  'IFS',
  'LOOKUP',
  'SQRT',
  'POW',
  'ABS',
  'MIN',
  'MAX',
  'ROUND',
  'CEIL',
  'FLOOR',
  'TRUE',
  'FALSE',
  'AND',
  'OR',
  'NOT',
  'E',
  'PI',
]);

/** Extrae los nombres de tabla usados como primer argumento de LOOKUP(...) */
function extractLookupTableNames(formula: string): Set<string> {
  const names = new Set<string>();
  const re = /LOOKUP\s*\(\s*([A-Z_][A-Z0-9_]*)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripStrings(formula))) !== null) {
    names.add(m[1].toUpperCase());
  }
  return names;
}

/** Quita las cadenas literales (entre comillas simples o dobles) para no romper extractVariables. */
function stripStrings(formula: string): string {
  return formula.replace(/"[^"]*"|'[^']*'/g, '');
}

/**
 * Evalúa una fórmula con los valores dados.
 * Usa expr-eval para evaluación segura (sin eval / new Function).
 *
 * @param formula      Expresión a evaluar
 * @param values       Map de variables disponibles (code → value)
 * @param lookupTables Tablas de búsqueda accesibles desde LOOKUP() en la fórmula
 */
export function evaluateFormula(
  formula: string,
  values: Record<string, any>,
  lookupTables: LookupTable[] = [],
  textCodes: ReadonlySet<string> = new Set()
): { value: any; error?: FormulaError } {
  // Validar que la fórmula no esté vacía
  if (!formula || formula.trim() === '') {
    return {
      value: null,
      error: {
        type: 'SYNTAX_ERROR',
        message: 'La fórmula no puede estar vacía',
        formula,
      },
    };
  }

  try {
    // Detectar variables faltantes antes de evaluar.
    // Excluir: strings literales, funciones reservadas, y nombres de tablas usadas en LOOKUP.
    const formulaNoStrings = stripStrings(formula);
    const tableNames = extractLookupTableNames(formula);
    const varPattern = /[A-Z_][A-Z0-9_]*/g;
    const usedVars = (formulaNoStrings.match(varPattern) || []).filter((v) => {
      const upper = v.toUpperCase();
      if (RESERVED_TOKENS.has(upper)) return false;
      if (tableNames.has(upper)) return false;
      return true;
    });
    const uniqueUsedVars = [...new Set(usedVars)];
    const missingVars = uniqueUsedVars.filter((v) => !(v in values) || values[v] == null);

    if (missingVars.length > 0) {
      const plural = missingVars.length > 1;
      return {
        value: null,
        error: {
          type: 'MISSING_VARIABLE',
          message: `${plural ? 'Variables sin valor asignado' : 'Variable sin valor asignado'}: ${missingVars.join(', ')}`,
          formula,
        },
      };
    }

    // Construir mapa de valores: numéricos para variables numéricas, strings para LOOKUP keys.
    // expr-eval acepta strings como argumentos de funciones, así que mantenemos el tipo original
    // cuando es string (necesario para LOOKUP(FREIGHT_TYPES, TIPO_FLETE) donde TIPO_FLETE es texto).
    const evalValues: Record<string, any> = {};
    Object.entries(values).forEach(([variable, value]) => {
      if (value == null) {
        evalValues[variable] = 0;
        return;
      }
      if (typeof value === 'string') {
        if (textCodes.has(variable)) {
          evalValues[variable] = value;
          return;
        }
        // Si el string parece un número, convertirlo. Si no, dejarlo como string.
        const numValue = Number(value);
        evalValues[variable] = isNaN(numValue) || value.trim() === '' ? value : numValue;
      } else {
        const numValue = Number(value);
        evalValues[variable] = isNaN(numValue) ? 0 : numValue;
      }
    });

    // También exponer los nombres de tabla como strings para que LOOKUP(FREIGHT_TYPES, ...)
    // resuelva el primer argumento.
    for (const tableName of tableNames) {
      if (!(tableName in evalValues)) {
        evalValues[tableName] = tableName;
      }
    }

    // Construir parser con las lookupTables del modelo
    const parser = lookupTables.length > 0 ? buildParser(lookupTables) : defaultParser;

    // Evaluar usando expr-eval
    const result = parser.evaluate(formula, evalValues);

    // Si el resultado es booleano, convertir a 1/0
    let numericResult: number;
    if (typeof result === 'boolean') {
      numericResult = result ? 1 : 0;
    } else if (typeof result === 'number') {
      numericResult = result;
    } else {
      numericResult = Number(result);
    }

    // Verificar resultado infinito (división por cero)
    if (!isFinite(numericResult)) {
      return {
        value: null,
        error: {
          type: 'DIVISION_BY_ZERO',
          message:
            'División por cero: el resultado es infinito. Verifique que los divisores no sean cero.',
          formula,
        },
      };
    }

    // Verificar resultado NaN
    if (isNaN(numericResult)) {
      return {
        value: null,
        error: {
          type: 'RUNTIME_ERROR',
          message: 'El resultado no es un número válido. Verifique los valores de las variables.',
          formula,
        },
      };
    }

    return { value: numericResult };
  } catch (error: any) {
    return {
      value: null,
      error: {
        type: 'SYNTAX_ERROR',
        message: translateEvalError(error.message),
        formula,
      },
    };
  }
}

/**
 * Calcula todos los valores de las variables en orden topológico
 *
 * @param variables    Variables del modelo (con .formula, .lookupTable, etc.)
 * @param inputValues  Valores de input (variables no calculadas, ENUM, TIERED resueltas, etc.)
 * @param lookupTables Tablas de lookup del modelo (para que LOOKUP() funcione en fórmulas)
 */
export function calculateVariables(
  variables: Variable[],
  inputValues: Record<string, any>,
  lookupTables: LookupTable[] = []
): CalculationResult[] {
  const results: CalculationResult[] = [];
  const calculatedValues: Record<string, any> = { ...inputValues };
  const textCodes = new Set(
    variables.filter((v) => v.variableType === VariableType.TEXT).map((v) => v.code!)
  );

  // Si no se pasaron lookupTables explícitamente, recolectar las que estén linkeadas
  // a Variables del modelo (Variable.lookupTable). Esto cubre el caso común de que
  // el caller pase solo Variable[] (back-compat con código existente).
  let effectiveTables = lookupTables;
  if (effectiveTables.length === 0) {
    const collected = new Map<string, LookupTable>();
    for (const v of variables) {
      if (v.lookupTable) {
        collected.set(v.lookupTable.id, v.lookupTable);
      }
    }
    if (collected.size > 0) effectiveTables = Array.from(collected.values());
  }

  // Ordenar variables en orden topológico
  const sortedCodes = topologicalSort(
    variables
      .filter((v) => v.code !== undefined)
      .map((v) => ({ code: v.code!, formula: v.formula }))
  );

  // Evaluar cada variable en orden
  sortedCodes.forEach((code) => {
    const variable = variables.find((v) => v.code === code);
    if (!variable) return;

    try {
      let value: any;
      let error: string | undefined;

      if (variable.variableType === VariableType.ENUM) {
        // ENUM: use input value directly (already numeric from dropdown)
        value = code in calculatedValues ? calculatedValues[code] : null;
      } else if (variable.variableType === VariableType.TIERED) {
        // TIERED: resolve based on CANTIDAD
        const quantity = calculatedValues['CANTIDAD'] ?? 1;
        if (variable.tiers && variable.tiers.length > 0) {
          const tierResult = resolveTieredVariable(variable.tiers, quantity);
          value = tierResult.unitPrice;
        } else {
          value = 0;
        }
      } else if (variable.variableType === VariableType.LOOKUP) {
        // LOOKUP: resolve using lookup table data
        if (variable.lookupTable?.data) {
          const data = variable.lookupTable.data;
          // Look for dimension values in the inputs
          const rowKey = calculatedValues[`${code}_ROW`] ?? data.rows[0] ?? '';
          const colKey = calculatedValues[`${code}_COL`] ?? data.cols[0] ?? '';
          value = resolveLookupVariable(data, rowKey, colKey);
        } else {
          value = null;
        }
      } else if (variable.formula) {
        // Variable calculada
        const result = evaluateFormula(
          variable.formula,
          calculatedValues,
          effectiveTables,
          textCodes
        );
        value = result.value;
        if (result.error) {
          error = result.error.message;
        }
      } else if (code in calculatedValues) {
        // Variable con valor de input
        value = calculatedValues[code];
      } else if (variable.defaultValue !== undefined) {
        // Variable con valor por defecto
        value = variable.defaultValue;
      } else {
        // Variable sin valor
        value = null;
      }

      // PERCENTAGE sin fórmula: almacenar como decimal en calculatedValues para que
      // fórmulas downstream calculen correctamente (10 → 0.10), pero mostrar el valor original
      if (variable.variableType === VariableType.PERCENTAGE && !variable.formula && value != null) {
        const numVal = Number(value);
        calculatedValues[code] = !isNaN(numVal) ? numVal / 100 : value;
      } else {
        calculatedValues[code] = value;
      }
      results.push({
        variableCode: code,
        value,
        error,
      });
    } catch (error: any) {
      results.push({
        variableCode: code,
        value: null,
        error: error.message,
      });
    }
  });

  return results;
}

/**
 * Calcula el precio sugerido basado en el modelo
 */
export function calculateSuggestedPrice(
  baseCost: number,
  targetMarginPercentage: number,
  taxes: Array<{ rate: number }>
): {
  baseCost: number;
  marginAmount: number;
  subtotal: number;
  taxesAmount: number;
  suggestedPrice: number;
} {
  // Costo base
  const marginAmount = baseCost * (targetMarginPercentage / 100);
  const subtotal = baseCost + marginAmount;

  // Calcular impuestos sobre el subtotal
  const totalTaxRate = taxes.reduce((sum, tax) => sum + tax.rate / 100, 0);
  const taxesAmount = subtotal * totalTaxRate;

  // Precio sugerido final
  const suggestedPrice = subtotal + taxesAmount;

  return {
    baseCost,
    marginAmount,
    subtotal,
    taxesAmount,
    suggestedPrice,
  };
}

/**
 * Calcula el margen real dado un precio final
 */
export function calculateActualMargin(
  baseCost: number,
  finalPrice: number,
  taxes: Array<{ rate: number }>
): {
  marginAmount: number;
  marginPercentage: number;
  isAboveFloor: boolean;
  isAboveTarget: boolean;
} {
  // Restar impuestos
  const totalTaxRate = taxes.reduce((sum, tax) => sum + tax.rate / 100, 0);
  const priceBeforeTax = finalPrice / (1 + totalTaxRate);

  // Calcular margen
  const marginAmount = priceBeforeTax - baseCost;
  const marginPercentage = (marginAmount / baseCost) * 100;

  return {
    marginAmount,
    marginPercentage,
    isAboveFloor: false, // Se calcula externamente con floor threshold
    isAboveTarget: false, // Se calcula externamente con target threshold
  };
}
